import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Refresh } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import Moment from 'react-moment';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Eye, User } from 'lucide-react';
import Loading from '../../common/loading';
import Table from '../../common/table';
import icClients from '../../../assets/icons/clients.svg?url';
import { ReactComponent as CopyIcon } from '../../../assets/icons/copy.svg';
import { Urls } from '../../../config/urls';
import l from '../../../lang';
import './styles.scss';
import MessageError from '../../common/message-error';
import TopDashboardSection from '../../common/top-dashboard-section';
import Card from '../../common/card';
import ClientsFilter from './filter';
import ExportClients from './export';
import ClientsStatsCards from './stats-cards';
import { EmptyNoFound } from './empty';
import { Routes } from '../../../config/routes';
import ButtonMoreMenu from '../../common/button-more-menu';
import { useClients } from '../../../store/clients';
import { Client } from '../../../store/clients/interfaces';
import { AuthSelectors } from '../../../store/auth/selectors';
import { canViewClients } from '../../../store/clients/roles';
import { Filters } from '../../../store/interfaces';
import { createDate, getMappedCountryCode } from '../../../utils/index';
import { countryName } from '../../../utils/payment-methods-utils';
import { formatTransactedAmount, getClientFullName, getClientInitial, getClientStatus } from './utils';

const initFilters: Filters = {
  search: '',
  sort_direction: 'desc',
  sort_by: 'transacted_amount',
  country: '',
  status: '',
};

const Clients = () => {
  const history = useHistory();
  const isTabletMobile = useMediaQuery('(max-width:768px)');
  const [filter, setFilter] = useState<Filters>(initFilters);
  const [copiedClientId, setCopiedClientId] = useState<number | null>(null);
  const { loading, error, allClients, summary, getClients, getClientsSummary, setClientDetail } = useClients();
  const roles = useSelector(AuthSelectors.getRoles);
  const canView = canViewClients(roles);

  useEffect(() => {
    if (canView) {
      getClients(0, filter, allClients?.size);
    }
  }, [canView, filter]);

  const hasFilter = Object.entries(filter).some(([key, value]) => 
    value !== '' && key !== 'sort_order' && key !== 'sort_by' && key !== 'sort_direction'
  );
  const dataExist = allClients && allClients?.data?.length > 0;

  const EmptyBody = () => {
    if (!error && !dataExist && !hasFilter) {
      return (
        <div className="clients__no-clients">
          <div>
            <img className="clients__no-clients__img" src={icClients} alt="img-no-clients" />
            <div>{l('clients.noClients')}</div>
          </div>
        </div>
      );
    }
    if (error || !dataExist) {
      return <EmptyNoFound />;
    }
    return null;
  };

  const renderDate = (date?: string) => {
    if (!date) {
      return <span>-</span>;
    }

    return <Moment format="DD/MM/YY">{createDate(date)}</Moment>;
  };

  const openClientDetail = (client: Client) => {
    setClientDetail(client);
    history.push(Routes.CLIENT_DETAIL.replace(':client_id', String(client.id)));
  };

  const renderRowActions = (client: Client) => (
    <span className="clients__actions_cell">
      <ButtonMoreMenu
        id={`client-actions-${client.id}`}
        items={[
          {
            content: (
              <span className="clients__menu_item">
                <Eye size={16} strokeWidth={1.75} aria-hidden />
                {l('clients.viewDetail')}
              </span>
            ),
            onClick: () => openClientDetail(client),
          },
        ]}
      />
    </span>
  );

  const renderStatus = (client: Client) => {
    const status = getClientStatus(client);
    const statusKey = status.toLowerCase();

    return (
      <span className={`clients__status clients__status--${statusKey}`}>
        <User className="clients__status-icon" size={14} strokeWidth={1.75} aria-hidden />
        <span>{l(`clients.status.${status}`)}</span>
      </span>
    );
  };

  return (
    <>
      <TopDashboardSection title={l('menu.item.CLIENTS')} subtitle={l('clients.list.subheadline')} />
      <Card className="clients">
        <div className="clients__content">
          {canView && (
            <>
              <ClientsStatsCards stats={summary.data} loading={summary.loading} />
              <div className="clients__filter_wrapper">
                <ClientsFilter
                  loading={loading}
                  appliedFilters={filter}
                  clearFilters={() => setFilter(initFilters)}
                  changeFilters={filters => setFilter(filters)}
                  exportButton={<ExportClients filters={filter} />}
                />
              </div>
              {loading && (
                <div className="clients__loading">
                  <Loading />
                </div>
              )}
              {error && (
                <div className="clients__error">
                  <MessageError iconClose={<Refresh />} onClose={() => getClients(0, filter, allClients?.size)} />
                </div>
              )}
              {!loading && !error && (
                <div className="clients__table">
                  <Table
                    idxHiddenColumnsOnMobile={[3, 4, 5]}
                    withBtnMenu
                    cellAlign="left"
                    headers={[
                      l('clients.client'),
                      l('clients.country'),
                      l('clients.email'),
                      <span className="clients__header-multiline" key="first-transaction">
                        <span>{l('clients.firstTransaction').split(' ')[0]}</span>
                        <span>{l('clients.firstTransaction').split(' ').slice(1).join(' ')}</span>
                      </span>,
                      <span className="clients__header-multiline" key="last-transaction">
                        <span>{l('clients.lastTransaction').split(' ')[0]}</span>
                        <span>{l('clients.lastTransaction').split(' ').slice(1).join(' ')}</span>
                      </span>,
                      l('clients.transactedAmount'),
                      l('clients.status'),
                    ]}
                    body={
                      (dataExist &&
                        allClients?.data?.map(client => {
                          const countryCode = getMappedCountryCode(client.country);

                          return [
                            <div className="clients__client-cell">
                              <span className="clients__avatar" aria-hidden>
                                {getClientInitial(client)}
                              </span>
                              <span className="clients__client-name">{getClientFullName(client)}</span>
                            </div>,
                            <div className="clients__country-cell">
                              <img
                                alt=""
                                className="clients__country-flag country-flag--rounded"
                                src={`${Urls.roundedFlags.replace(':code', countryCode)}`}
                              />
                              <span>{countryName(client.country)}</span>
                            </div>,
                            <div className="clients__email-cell">
                              {client.email ? (
                                <CopyToClipboard
                                  text={client.email}
                                  onCopy={() => setCopiedClientId(client.id)}
                                >
                                  <Tooltip
                                    onMouseEnter={() => setCopiedClientId(null)}
                                    title={l(copiedClientId === client.id ? 'common.copied' : 'common.copy')}
                                  >
                                    <button type="button" className="clients__email-copy">
                                      <CopyIcon className="clients__email-copy-icon" aria-hidden />
                                      <span>{client.email}</span>
                                    </button>
                                  </Tooltip>
                                </CopyToClipboard>
                              ) : (
                                <span>-</span>
                              )}
                            </div>,
                            <span className="clients__date-cell">{renderDate(client.firstTransactionDate)}</span>,
                            <span className="clients__date-cell">{renderDate(client.lastTransactionDate)}</span>,
                            <span className="clients__amount-cell">
                              {formatTransactedAmount(
                                client.transactedAmount,
                                client.transactedCurrency || client.currency
                              )}
                            </span>,
                            renderStatus(client),
                            renderRowActions(client),
                          ];
                        })) ||
                      null
                    }
                    rowsPerPageOptions={[10, 25, 50]}
                    rowsLength={allClients?.totalElements}
                    rowsPerPage={allClients?.size ?? 10}
                    page={allClients?.page}
                    handleChangePage={(event, newPage) => {
                      getClients(newPage, filter, allClients?.size);
                    }}
                    handleChangeRowsPerPage={event => {
                      getClients(0, filter, Number(event.target.value));
                    }}
                    maxHeight={undefined}
                    emptyBody={<EmptyBody />}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </Card>
    </>
  );
};

export default Clients;
